/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */

/**
 * The component of ExpensesAnalyzer page for render and controls Charts
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.1.0
 */

import { useRef } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import moment, { Moment } from 'moment';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELinkTarget } from '@/main/components/common/ExternalLink/ExternalLink';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectAuthExpenses, makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { stringFormatParamsHtml } from '@/main/utils/stringFormat';
import { Chart, ExternalLink, RangeMonthPicker } from '@components/common';
import { LayoutStyled } from '@components/common/Block/Layout';
import { ChartCategoryPadding } from '@components/common/Chart/ChartsImplementation/ChartCategory/ChartCategory';
import ChartCategoryLabel, {
    IChartBarLabel,
} from '@components/common/Chart/ChartsImplementation/ChartCategory/ChartCategoryLabel';
import { MapChartCategoryNameToColor } from '@components/common/Chart/ChartsImplementation/ChartCategory/chartCategoryTypes';
import ChartDynamicLabel from '@components/common/Chart/ChartsImplementation/ChartDynamic/ChartDynamicLabel';
import { EChartImplementationType } from '@components/common/Chart/ChartsImplementation/chartImplementationTypes';
import {
    EChartDataKeyEnum,
    IChartCategoryRange,
    IChartDataInput,
    IChartLabels,
    TMapNameToColor,
    TMapNameToTranslation,
} from '@components/common/Chart/chartTypes';
import { setRangeMonthFilter } from '@components/Table/actions/tableActions';
import { ITableReducer } from '@components/Table/reducers/tableReducer';
import { makeSelectorTableFilter } from '@components/Table/selectors/tableSelectors';
import { ISetRangeMonthFilterPayload, TableNameType } from '@components/Table/types/tableTypes';
import initTranslationCommon from '@modules/internationalization/constants/common';
import { initTranslationPageExpensesAnalyzer } from '@modules/internationalization/constants/pages/expensesAnalyzer';
import { IPageExpensesAnalyzerTranslation } from '@modules/internationalization/types';
import ICommonTranslation from '@modules/internationalization/types/common';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import { IRootReducer } from '@reducers/rootReducer';
import { formatDateRangeToMMYYYY } from '@utils/formatText';

import {
    setCurrentChartExpensesAnalyzerAction,
    updateChartItemExpensesAnalyzerAction,
} from '../../pageExpensesAnalyzerActions';
import {
    makeSelectorExpensesAnalyzerCategoryData,
    makeSelectorExpensesAnalyzerChartCategoryRange,
    makeSelectorExpensesAnalyzerChartIsLoading,
    makeSelectorExpensesAnalyzerChartLabels,
    makeSelectorExpensesAnalyzerCurrentChart,
    makeSelectorExpensesAnalyzerDynamicData,
    makeSelectorExpensesAnalyzerError,
    makeSelectorExpensesAnalyzerSelectedId,
    makeSelectorExpensesAnalyzerType,
} from '../../pageExpensesAnalyzerSelectors';
import { MapExpensesAnalyzerTypeToMeta, mapExpensesTypeToOnboarding } from '../../types/pageExpensesAnalyzerMapTypes';
import { EExpensesAnalyzerTypes } from '../../types/pageExpensesAnalyzerTypes';
import { ExpensesAnalyzerChartDynamicColors } from './ExpensesAnalyzerChartTab/constants/expensesAnalyzerChartDynamicConfiguration';
import ExpensesAnalyzerChartTab from './ExpensesAnalyzerChartTab/ExpensesAnalyzerChartTab';
import { EExpensesAnalyzerChart } from './expensesAnalyzerControllerTypes';
import {
    ExpensesAnalyzerChartContainerStyled,
    ExpensesAnalyzerControllerStyled,
    ExpensesPreliminaryDataContainer,
} from './styled/ExpensesAnalyzerControllerStyled';

interface IExpensesAnalyzerControllerConnectedProps {
    chartCategoryRange: IChartCategoryRange;
    chartDataCategory: IChartDataInput[];
    chartDataDynamic: IChartDataInput[];
    chartLabels: IChartLabels;
    currentChart: EExpensesAnalyzerChart;
    error: string;
    expensesAnalyzerType: EExpensesAnalyzerTypes;
    isChartLoading: boolean;
    selectedFilters: ITableReducer[TableNameType]['filter'];
    selectedId: number;
}

interface IExpensesAnalyzerControllerDispatchProps {
    setCurrentChart: (payload: EExpensesAnalyzerChart) => void;
    setRangeMonthFilter: (payload: ISetRangeMonthFilterPayload) => void;
    updateChartData: (payload: IChartDataInput) => void;
}

interface IExpensesAnalyzerControllerProps {
    isDynamicEnabled: boolean;
    isShowChart: boolean;
    chartRenderCallback: (state: boolean) => void;
    translation: IPageExpensesAnalyzerTranslation & ICommonTranslation;
}

type TExpensesAnalyzerControllerProps = IExpensesAnalyzerControllerProps &
    IExpensesAnalyzerControllerConnectedProps &
    IExpensesAnalyzerControllerDispatchProps;

const ExpensesAnalyzerController = (props: TExpensesAnalyzerControllerProps) => {
    // Define month range date
    const startDate = (props.selectedFilters as any)?.monthRange?.valueFrom
        ? moment((props.selectedFilters as any)?.monthRange?.valueFrom, EFormatData.YearMonthReverse).startOf('month')
        : moment().startOf('month');
    const endDate = (props.selectedFilters as any)?.monthRange?.valueTo
        ? moment((props.selectedFilters as any)?.monthRange?.valueTo, EFormatData.YearMonthReverse).endOf('month')
        : moment().endOf('month');

    const chartStartDate = props.chartCategoryRange.from ? moment(props.chartCategoryRange.from) : startDate;
    const chartEndDate = props.chartCategoryRange.to ? moment(props.chartCategoryRange.to) : endDate;

    const isPreliminaryDataFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.EXPENSES_PRELIMINARY_DATA),
    );

    const authExpensesData = useSelector(makeSelectAuthExpenses());

    /*
     * -------------------------------------------
     *           CHART
     * -------------------------------------------
     */

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartLabel: string = formatDateRangeToMMYYYY(chartStartDate, chartEndDate, props.translation.calendar.months);

    const chartCategoryLabel: IChartBarLabel[] = props.chartDataCategory.length
        ? Object.keys(props.chartDataCategory[0][EChartDataKeyEnum.y]).map((key) => ({
              title: props.chartLabels[key]?.title ?? key,
              subTitle: props.chartLabels[key]?.subTitle ?? '',
              labelType: props.chartLabels[key]?.labelType,
          }))
        : [];

    const chartDynamicColors: TMapNameToColor = {};
    const chartDynamicTranslation: TMapNameToTranslation = {};

    props.chartDataDynamic.forEach((data: IChartDataInput, index: number) => {
        chartDynamicColors[data[EChartDataKeyEnum.x]] = ExpensesAnalyzerChartDynamicColors[index];
        chartDynamicTranslation[data[EChartDataKeyEnum.x]] = data[EChartDataKeyEnum.x];
    });

    /*
     * -------------------------------------------
     *           DATE PICKER
     * -------------------------------------------
     */

    const datePickerLabel = formatDateRangeToMMYYYY(startDate, endDate, props.translation.calendar.months);

    const onDatePickerClick = (rangeStart: Moment, rangeEnd: Moment): void => {
        props.setRangeMonthFilter({
            filter: {
                filterValue: datePickerLabel,
                valueFrom: rangeStart.format(EFormatData.YearMonthReverse),
                valueTo: rangeEnd.format(EFormatData.YearMonthReverse),
            },
        });
    };

    return (
        <LayoutStyled>
            {isPreliminaryDataFeatureEnabled && (
                <ExpensesPreliminaryDataContainer>
                    <Typography>
                        {stringFormatParamsHtml(props.translation.preliminaryData, {
                            link: (
                                <ExternalLink
                                    href={authExpensesData?.urls.preliminaryData ?? '#'}
                                    target={ELinkTarget.BLANK}
                                >
                                    {props.translation.preliminaryDataLink}
                                </ExternalLink>
                            ),
                        })}
                    </Typography>
                </ExpensesPreliminaryDataContainer>
            )}
            {props.isDynamicEnabled && (
                <ExpensesAnalyzerControllerStyled className="py-3 px-3 mb-3">
                    <RangeMonthPicker
                        withPresets
                        label={datePickerLabel}
                        rangeEnd={endDate}
                        rangeStart={startDate}
                        onOkClick={onDatePickerClick}
                    />
                    <ExpensesAnalyzerChartTab
                        currentChart={props.currentChart}
                        expensesAnalyzerType={props.expensesAnalyzerType}
                        translation={props.translation.controller}
                        onTabClick={props.setCurrentChart}
                    />
                </ExpensesAnalyzerControllerStyled>
            )}
            <ExpensesAnalyzerChartContainerStyled
                ref={chartContainerRef}
                {...{ [ONBOARDING_DATA_ATTR]: mapExpensesTypeToOnboarding[props.expensesAnalyzerType]?.chart }}
            >
                {props.isShowChart &&
                    {
                        [EExpensesAnalyzerChart.CATEGORY]: (
                            <Chart
                                chartRenderCallback={props.chartRenderCallback}
                                currency={props.translation.currency}
                                data={props.chartDataCategory}
                                date={chartLabel}
                                isEmpty={props.chartDataCategory.length === 0}
                                isError={props.error.length !== 0}
                                isHideTooltipDate={
                                    MapExpensesAnalyzerTypeToMeta[props.expensesAnalyzerType].isBillsType
                                }
                                isLoading={props.isChartLoading}
                                mapIdToLabel={props.chartLabels}
                                mapNameToColor={MapChartCategoryNameToColor}
                                mapNameToTranslation={props.translation.chart.category}
                                translation={props.translation}
                                type={EChartImplementationType.category}
                                width={chartContainerRef.current?.offsetWidth ?? 0}
                                onLegendClickHandler={props.updateChartData}
                            >
                                {chartContainerRef.current && (
                                    <ChartCategoryLabel
                                        labels={chartCategoryLabel}
                                        width={chartContainerRef.current.offsetWidth - ChartCategoryPadding.left}
                                    />
                                )}
                            </Chart>
                        ),
                        [EExpensesAnalyzerChart.DYNAMIC]: props.isDynamicEnabled && (
                            <Chart
                                chartRenderCallback={props.chartRenderCallback}
                                currency={props.translation.currency}
                                data={props.chartDataDynamic}
                                date={chartLabel}
                                isEmpty={props.chartDataDynamic.length === 0}
                                isError={props.error.length !== 0}
                                isLoading={props.isChartLoading}
                                mapIdToLabel={props.chartLabels}
                                mapNameToColor={chartDynamicColors}
                                mapNameToTranslation={chartDynamicTranslation}
                                translation={props.translation}
                                type={EChartImplementationType.dynamic}
                                width={chartContainerRef.current?.offsetWidth ?? 0}
                                onLegendClickHandler={props.updateChartData}
                            >
                                <ChartDynamicLabel item={props.chartDataDynamic[0]} />
                            </Chart>
                        ),
                    }[props.currentChart]}
            </ExpensesAnalyzerChartContainerStyled>
        </LayoutStyled>
    );
};

ExpensesAnalyzerController.defaultProps = {
    translation: { ...initTranslationPageExpensesAnalyzer, ...initTranslationCommon },
};

const mapStateToProps = createStructuredSelector<IRootReducer, IExpensesAnalyzerControllerConnectedProps>({
    chartCategoryRange: makeSelectorExpensesAnalyzerChartCategoryRange(),
    chartDataCategory: makeSelectorExpensesAnalyzerCategoryData(),
    chartDataDynamic: makeSelectorExpensesAnalyzerDynamicData(),
    chartLabels: makeSelectorExpensesAnalyzerChartLabels(),
    currentChart: makeSelectorExpensesAnalyzerCurrentChart(),
    error: makeSelectorExpensesAnalyzerError(),
    expensesAnalyzerType: makeSelectorExpensesAnalyzerType(),
    isChartLoading: makeSelectorExpensesAnalyzerChartIsLoading(),
    selectedFilters: makeSelectorTableFilter(),
    selectedId: makeSelectorExpensesAnalyzerSelectedId(),
});

const mapDispatchToProps = (dispatch: Dispatch): IExpensesAnalyzerControllerDispatchProps => ({
    setCurrentChart: (payload) => dispatch(setCurrentChartExpensesAnalyzerAction(payload)),
    setRangeMonthFilter: (payload) => dispatch(setRangeMonthFilter(payload)),
    updateChartData: (payload) => dispatch(updateChartItemExpensesAnalyzerAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesAnalyzerController);
